'use client';

import type { GeneratorShowcaseBlockConfig } from '@/cms/blocks/catalog/whats-more/whats-more-6';
import { CmsImage } from '@/cms/components/blocks/shared/cms-image';
import { CmsImageFrame } from '@/cms/components/blocks/shared/cms-image-frame';
import { CmsSectionShell } from '@/cms/components/blocks/shared/cms-section-shell';
import { CmsSurface } from '@/cms/components/blocks/shared/cms-surface';
import { CmsTitleGroup } from '@/cms/components/blocks/shared/cms-title-group';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { useEffect, useState } from 'react';

type GeneratorShowcaseItem = GeneratorShowcaseBlockConfig['items'][number];

function rotateItems(items: GeneratorShowcaseItem[], startIndex: number) {
  if (items.length === 0) {
    return [];
  }

  return items.map((_, index) => items[(startIndex + index) % items.length]!);
}

export function GeneratorShowcaseBlockClient({
  blockId,
  config,
}: {
  blockId: string;
  config: GeneratorShowcaseBlockConfig;
}) {
  const [startIndex, setStartIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<
    'forward' | 'backward' | null
  >(null);

  useEffect(() => {
    setStartIndex(0);
    setSlideDirection(null);
  }, [blockId, config]);

  const arrangedItems = rotateItems(config.items, startIndex);
  const canShift = config.items.length > 1;

  const shiftCards = (direction: 'forward' | 'backward') => {
    if (!canShift) {
      return;
    }

    setSlideDirection(direction);
    setStartIndex((currentIndex) => {
      const nextIndex =
        direction === 'forward' ? currentIndex + 1 : currentIndex - 1;

      return (nextIndex + config.items.length) % config.items.length;
    });
  };

  return (
    <CmsSectionShell
      as="section"
      id={blockId}
      width="default"
      spacing="default"
      className="cms-block-whats-more-whats-more-6-whats-more-6__section"
    >
      <CmsTitleGroup
        eyebrow={config.eyebrow}
        title={config.title}
        align="center"
        className="cms-block-whats-more-whats-more-6-whats-more-6__header"
      />

      <div className="cms-block-whats-more-whats-more-6-whats-more-6__carousel">
        <button
          type="button"
          className="cms-block-whats-more-whats-more-6-whats-more-6__navButton cms-block-whats-more-whats-more-6-whats-more-6__navPrev"
          aria-label={config.previousArrangementLabel}
          onClick={() => shiftCards('backward')}
          disabled={!canShift}
        >
          {renderAppIcon({
            name: 'chevron-left',
            className:
              'cms-block-whats-more-whats-more-6-whats-more-6__navIcon',
          })}
        </button>

        <div className="cms-block-whats-more-whats-more-6-whats-more-6__cardsViewport">
          <div
            key={`${startIndex}-${slideDirection ?? 'idle'}`}
            className={`cms-block-whats-more-whats-more-6-whats-more-6__cards ${
              slideDirection === 'forward'
                ? 'cms-block-whats-more-whats-more-6-whats-more-6__cardsSlideForward'
                : slideDirection === 'backward'
                  ? 'cms-block-whats-more-whats-more-6-whats-more-6__cardsSlideBackward'
                  : ''
            }`}
          >
            {arrangedItems.map((item, index) => (
              <CmsSurface
                as="article"
                key={`${blockId}-${index}-${item.title}`}
                variant="card"
                className="cms-block-whats-more-whats-more-6-whats-more-6__card"
              >
                <CmsImageFrame
                  ratio="landscape"
                  frameClassName="cms-block-whats-more-whats-more-6-whats-more-6__mediaFrame"
                >
                  <CmsImage
                    src={item.image}
                    alt={item.title}
                    className="cms-block-whats-more-whats-more-6-whats-more-6__mediaImage"
                  />
                </CmsImageFrame>

                <div className="cms-block-whats-more-whats-more-6-whats-more-6__cardDivider" />
                <h3 className="cms-title-item cms-block-whats-more-whats-more-6-whats-more-6__cardTitle">
                  {item.title}
                </h3>
              </CmsSurface>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="cms-block-whats-more-whats-more-6-whats-more-6__navButton cms-block-whats-more-whats-more-6-whats-more-6__navNext"
          aria-label={config.nextArrangementLabel}
          onClick={() => shiftCards('forward')}
          disabled={!canShift}
        >
          {renderAppIcon({
            name: 'chevron-right',
            className:
              'cms-block-whats-more-whats-more-6-whats-more-6__navIcon',
          })}
        </button>
      </div>
    </CmsSectionShell>
  );
}
